import { makeLogger } from '../../../logger/logger';
import type { GuidedLinkChip } from '../api';

const { err } = makeLogger({
  pathToCurrentFile: 'components/guided/backends/utils.ts',
  loggersList: ['err']
});

export function parseWordsList(words: string): Array<string> {
    if (!words)
      return [];

    try {
      return words.indexOf('[') !== -1 ?
        JSON.parse(words)
        :
        words
          .split(',')
          .map(w => w.trim())
      ;

    } catch (e) {
      err('Failed to parse word list', words, e);
      return [];
    }
}

//courtesy: https://github.com/aceakash/string-similarity/blob/master/src/index.js
export function compareTwoStrings(first: string, second: string): number {
    first = first.replace(/\s+/g, '');
    second = second.replace(/\s+/g, '');

    if (first === second) return 1; // identical or empty
    if (first.length < 2 || second.length < 2) return 0; // if either is a 0-letter or 1-letter string

    const firstBigrams = new Map();
    for (let i = 0; i < first.length - 1; i++) {
      const bigram = first.substring(i, i + 2);
      const count = firstBigrams.has(bigram)
        ? firstBigrams.get(bigram) + 1
        : 1;

      firstBigrams.set(bigram, count);
    }


    let intersectionSize = 0;
    for (let i = 0; i < second.length - 1; i++) {
      const bigram = second.substring(i, i + 2);
      const count = firstBigrams.has(bigram)
        ? firstBigrams.get(bigram)
        : 0;

      if (count > 0) {
        firstBigrams.set(bigram, count - 1);
        intersectionSize++;
      }
    }

    return (2.0 * intersectionSize) / (first.length + second.length - 2);
}

export function highlight(term: string, word: string, opening = '<span class="highlight">',closing = '</span>'): string {

    const values = term
      .split(' ')
      .filter(str => str.length > 1)
      .map(str => escapeRegexp(str))
      .join('|');

    // g - global = match any occurance
    // i - case insensitive,
    // u - unicode, excapeRegexp returns escaped unicode points
    return word.replace(new RegExp(values, 'gui'), function (x) {
        return opening + x + closing;
    });
}

export function highlightByTags(text: string, opening = '<span class="highlight">', closing = '</span>'): string {
    if(!text) return '';
    const textToHighlight = text.match(/\{0\}.*?\{1\}/g)?.[0];
  
    const highlightedText = text.replace(textToHighlight, `${opening}${textToHighlight}${closing}`)
      .replace('{0}', '').replace('{1}', '');
    return highlightedText;
}

//courtesy: https://stackoverflow.com/questions/3561493/is-there-a-regexp-escape-function-in-javascript
//direct permalink to answer: https://stackoverflow.com/a/57541440
/**
 * Escapes string for use in regular expression
 * Use with "u" mode to properly recognize unicode points
 * @param input
 */
export function escapeRegexp(input: string): string {
    return Array.from(input)
      .map(char => `\\u{${char.charCodeAt(0).toString(16)}}`)
      .join('');
}


interface getFromJsonInterface{
    data: string,
    fallback?: any, //eslint-disable-line @typescript-eslint/no-explicit-any
    message?:string
}
export const getFromJsonOrFallback = ({data, fallback = {}, message = ''}:getFromJsonInterface) => {
    try{
      return JSON.parse(data.replace(/'/g,'"'));
    }catch(e){
      err('Failed to parse JSON will use default value', data, message);
      return fallback;
    }
};

export const isEmptyObject = (obj) => {
    for (const i in obj) return false;
    return true;
};

export const chipsBeforeForCategoryCreator = (
  item,
  category: string,
  config: Array<{
    textKey: string,
    textNestedKey?: string,
    fromNested?: boolean,
    jsonDecode?: boolean,
    icon?: string,
    label?: string
    cssClass?: string
}>
) => {
    const buildLinkChip = (text: string, icon?: string | undefined, label?: string | undefined, cssClass?: string | undefined) => {
        const chip = { text };

        if (icon) {
          chip['icon'] = icon;
        }

        if (label) {
          chip['label'] = label;
        }

        if (cssClass) {
          chip['cssClass'] = cssClass;
        }

        return chip;
    };

    const chips = {
      beforeTitle: []
    };

    if (item['_routing'] && item['_routing'] === category) {
      config.forEach(configElement => {
          let guidedlinkChip: GuidedLinkChip;

          if (!configElement.fromNested && !configElement.jsonDecode) {
            if (item[configElement.textKey]) {
              guidedlinkChip = buildLinkChip(
                item[configElement.textKey],
                configElement.icon,
                configElement.label
              );
            }
          }

          if (configElement.fromNested && configElement.textNestedKey && !configElement.jsonDecode) {
            if (item[configElement.textKey] && item[configElement.textKey][configElement.textNestedKey]) {
              guidedlinkChip = buildLinkChip(
                item[configElement.textKey][configElement.textNestedKey],
                configElement.icon,
                configElement.label
              );
            }
          }

          if (configElement.fromNested && configElement.textNestedKey && configElement.jsonDecode) {
            if (item[configElement.textKey]) {
              const nestedObject = JSON.parse(item[configElement.textKey]);
              if (nestedObject[configElement.textNestedKey]) {
                guidedlinkChip = buildLinkChip(
                  nestedObject[configElement.textNestedKey],
                  configElement.icon,
                  configElement.label
                );
              }
            }
          }

          if (guidedlinkChip) {
            chips.beforeTitle = [
              ...chips.beforeTitle,
              ...[guidedlinkChip]
            ];
          }
      });
    }

    return chips;
};

export const parseJsonWithFallback = <T> (data: string,fallback: T, failMessage?: string): T => {

    try{
      const parsed = JSON.parse(data);
      return typeof parsed === 'object' ? parsed as T : fallback;
    }catch(e){
      err(failMessage ?? 'Failed to parse JSON %s returning fallback %o', data, fallback, e);
      return fallback;
    }

};
